<template>
  <Loader :value="promise" :class="['site-navigation', { 'site-navigation--disabled': disabled }]">
    <div v-if="visibleItems.length" class="site-navigation__header">
      <span>{{ $t('menuItemName') }}</span>
      <span>{{ $t('menuItemLinksTo') }}</span>
    </div>

    <Draggable
      :list="items"
      v-bind="dragOptions"
      class="site-navigation__list"
      @start="$ev => (dragging = items[$ev.oldIndex])"
      @end="onDragEnd"
    >
      <div
        v-for="(item, index) in items"
        v-show="isAvailable(item)"
        :key="index"
        :class="['site-navigation__item', { 'site-navigation__item--hidden': item.hideInMenu }]"
      >
        <Row
          :item="item"
          :label="getLabel(item)"
          :target="getTarget(item)"
          :position="visibleItems.indexOf(item) + 1"
          :actions="getActions(item)"
          :disabled="disabled"
          @edit="openItem({ item })"
        />

        <Draggable
          v-if="nested && !item.key"
          :list="item.items"
          v-bind="dragOptions"
          :class="['site-navigation__children', { 'site-navigation__children--rejected': rejected === item.items }]"
          @start="$ev => (dragging = item.items[$ev.oldIndex])"
          @end="onDragEnd"
          @dragover.native="onChildrenDragOver"
        >
          <div
            v-for="(child, childIndex) in item.items"
            :key="childIndex"
            :class="['site-navigation__item', { 'site-navigation__item--hidden': child.hideInMenu }]"
          >
            <Row
              :item="child"
              :label="child.label"
              :target="getTarget(child)"
              :actions="getActions(child, item)"
              :disabled="disabled"
              @edit="openItem({ item: child, parent: item })"
            />
          </div>

          <template v-if="item.items.length || isGroup(item)" #footer>
            <button
              type="button"
              class="site-navigation__add-child"
              :disabled="disabled"
              @click="openItem({ parent: item })"
            >
              <Icon icon="fa-pro-light:plus" />
              <span>{{ $t('menuItemAddChild') }}</span>
            </button>
          </template>
        </Draggable>
      </div>
    </Draggable>

    <div v-if="!visibleItems.length" class="site-navigation__empty">{{ $t('menuItemsEmpty') }}</div>

    <ButtonComponent
      class="site-navigation__add"
      :label="$t('menuItemCreate')"
      icon="fa-pro-solid:plus-circle"
      :disabled="disabled"
      @click="openItem()"
    />

    <ItemModal
      v-if="modal"
      :value="modal.item"
      :systemItem="modal.item && getSystemItem(modal.item.key)"
      :isChild="Boolean(modal.parent)"
      :pages="pages"
      :anchors="anchors"
      :units="units"
      :type="type"
      :nested="nested"
      :variants="variants"
      :visibility="visibility"
      :placements="placements"
      @submit="submit"
      @close="modal = null"
    />

    <ModalComponent :show="Boolean(remove)" clickaway @close="remove = null">
      <Card variant="prompt" :title="$t('menuItemDeleteTitle')">
        <div v-if="remove">{{ $t('menuItemDeleteDescription', { label: getLabel(remove.item) }) }}</div>
        <template #actions>
          <ButtonComponent :label="$t('cancel')" @click="remove = null" />
          <ButtonComponent :label="$t('delete')" icon="fa-pro-light:trash" theme="error" @click="confirmRemove" />
        </template>
      </Card>
    </ModalComponent>
  </Loader>
</template>

<script>
import { omit, path } from 'ramda'
import Draggable from 'vuedraggable'
import { ModalComponent } from 'vue-elder-modal'
import { Clone } from '@/utils'
import fetch from '@/fetch'
import graphql from '@/api/graphql'
import CustomFieldSchemasQuery from '@/api/graphql/queries/CustomFieldSchemas.gql'
import Card from '@/components/Card-v2'
import { getBlocks } from '@/components/FieldBuilder/utils'
import ItemModal from './ItemModal'
import Row from './Row'

// keep in sync with the system item conditions in nuxt-templates (useProjectNavigation, standard-page)
const Availability = {
  home: (data, { hasHomeItem }) => hasHomeItem,
  flatfinder: data => {
    const settings = path(['customFields', 'flatfinder'], data) || {}

    return (
      !(settings['hide-flatfinder'] && settings['hide-residentials']) &&
      data.type !== 'singular' &&
      Boolean(path(['flatfinders', 'length'], data)) &&
      path(['stats', 'count'], data) > 0
    )
  },
  attachments: data => data.type !== 'singular' && Boolean(path(['media', 'attachments', 'length'], data)),
  brochure: data => Boolean(path(['media', 'brochure', 'length'], data)),
  socialMedia: data =>
    Object.entries(path(['customFields', 'social-media'], data) || {}).some(
      ([key, value]) => key.endsWith('_url') && value,
    ),
  contact: data => !path(['customFields', 'contactPageSettings', 'hideContactPage'], data),
}

const LinkIcons = {
  page: 'fa-pro-light:file',
  unit: 'fa-pro-light:house',
  url: 'fa-pro-light:arrow-up-right-from-square',
  path: 'fa-pro-light:link',
}

const LayoutPaths = { Project: 'themeLayout', Page: 'theme.layout' }

const withItems = item => (item.key ? item : { ...item, items: (item.items || []).map(withItems) })

const getSchema = source => {
  const [, schema, type] = (source || '').split('.')
  return type === 'template' ? schema : 'subpage'
}

const getAnchors = (fields, placement, layout = []) => {
  const blocks = getBlocks(fields, placement).filter(block => !block.fixed)
  if (!layout.length) return blocks

  return layout
    .map(entry => {
      const block = blocks.find(block => [path(['data', 'reference'], entry), entry.key].includes(block.key))
      return block && { ...block, key: entry.key }
    })
    .filter(Boolean)
}

export default {
  name: 'SiteNavigation',
  props: {
    value: Object,
    schemaData: Object,
    type: String,
    systemItems: Array,
    disabled: Boolean,
    nested: {
      type: Boolean,
      default: true,
    },
    variants: {
      type: Boolean,
      default: true,
    },
    visibility: {
      type: Boolean,
      default: true,
    },
    placements: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    navigation: {
      handler() {
        const keys = this.navigation.items.map(item => item.key)
        const missing = this.systemItems.filter(item => !keys.includes(item.key))

        this.items = Clone([
          ...missing.filter(item => item.prepend).map(item => ({ key: item.key })),
          ...this.navigation.items,
          ...missing.filter(item => !item.prepend).map(item => ({ key: item.key })),
        ]).map(withItems)
      },
      immediate: true,
    },
  },
  data() {
    return {
      items: [],
      posts: [],
      units: [],
      schemas: {},
      hasHomeItem: false,
      promise: null,
      modal: null,
      remove: null,
      dragging: null,
      rejected: null,
    }
  },
  computed: {
    navigation() {
      return this.value || { items: [] }
    },

    visibleItems() {
      return this.items.filter(this.isAvailable)
    },

    pages() {
      return [
        { id: 'home', label: this.$t('frontPage') },
        { id: 'contact', label: this.$t('contactPage'), condition: this.type === 'Page' },
        ...this.posts.map(post => ({ ...post, sublabel: this.$tc(post.schema, 1) })),
      ].filter(page => !('condition' in page) || page.condition)
    },

    anchors() {
      const fields = this.schemaData.customFieldsConfig.flatMap(config =>
        config.schema.map(field => ({ ...field, placement: config.placement || field.placement })),
      )
      const layout = this.$path(LayoutPaths[this.type], this.schemaData)

      return {
        page: {
          home: getAnchors(fields, ['presentation', `${this.type.toLowerCase()}:presentation`], layout),
          contact: [],
          ...Object.fromEntries(
            this.posts.map(post => [post.id, getAnchors(this.schemas[post.source] || [], 'post', post.layout)]),
          ),
        },
      }
    },

    dragOptions() {
      return {
        group: 'site-navigation',
        draggable: '.site-navigation__item',
        filter: '.elder-dropdown__content',
        preventOnFilter: false,
        ghostClass: 'site-navigation__item--ghost',
        animation: 150,
        emptyInsertThreshold: 0,
        disabled: this.disabled,
        move: this.canMove,
      }
    },
  },
  methods: {
    getSystemItem(key) {
      return this.systemItems.find(item => item.key === key)
    },

    isAvailable(item) {
      if (!item.key) return true
      if (!this.getSystemItem(item.key)) return false
      return !Availability[item.key] || Availability[item.key](this.schemaData, this)
    },

    isGroup(item) {
      return !item.link || item.link.type === 'none'
    },

    canBeChild(item) {
      return !item.key && !item.items.length
    },

    getLabel(item) {
      if (item.label) return item.label
      const system = this.getSystemItem(item.key)
      return system && system.label
    },

    getTarget(item) {
      const { type, value, anchor } = item.link || {}
      const Targets = {
        page: () => this.pages.find(page => page.id === value),
        unit: () => this.units.find(unit => unit.id === value),
        url: () => ({ label: value }),
      }

      if (!Targets[type]) return { label: this.$t('menuItemOpensSubmenu'), icon: 'fa-pro-light:folder' }

      const target = Targets[type]()
      if (!target)
        return { label: this.$t('menuItemLinkMissing'), icon: 'fa-pro-light:triangle-exclamation', isMissing: true }

      const block = (path([type, value], this.anchors) || []).find(block => block.key === anchor)
      const isPath = type === 'url' && /^[/#]/.test(value)
      return {
        label: block ? `${target.label} › ${block.label}` : target.label,
        icon: LinkIcons[isPath ? 'path' : type],
      }
    },

    getActions(item, parent) {
      const list = parent ? parent.items : this.items
      const siblings = list.filter(this.isAvailable)
      const previous = siblings[siblings.indexOf(item) - 1]
      const next = siblings[siblings.indexOf(item) + 1]
      const previousLabel = previous && this.getLabel(previous)
      const isPreviousSystem = Boolean(previous && previous.key)

      return [
        [
          { label: this.$t('edit'), icon: 'fa-pro-light:pen', onClick: () => this.openItem({ item, parent }) },
          {
            label: this.$t('menuItemAddChild'),
            icon: 'fa-pro-light:plus',
            condition: this.nested && !parent && !item.key,
            onClick: () => this.openItem({ parent: item }),
          },
        ],
        [
          {
            label: this.$t('menuItemMoveUp'),
            icon: 'fa-pro-light:arrow-up',
            disabled: !previous,
            onClick: () => this.move(item, list, list, list.indexOf(previous)),
          },
          {
            label: this.$t('menuItemMoveDown'),
            icon: 'fa-pro-light:arrow-down',
            disabled: !next,
            onClick: () => this.move(item, list, list, list.indexOf(next)),
          },
          {
            label: this.$t('menuItemMoveUnder', { label: previousLabel }),
            icon: 'fa-pro-light:arrow-turn-down-right',
            condition: this.nested && !parent && previous && this.canBeChild(item),
            disabled: isPreviousSystem,
            tooltip: isPreviousSystem ? this.$t('menuItemCannotHaveChildren', { label: previousLabel }) : null,
            onClick: () => this.move(item, list, previous.items, previous.items.length),
          },
          {
            label: this.$t('menuItemOutdent'),
            icon: 'fa-pro-light:arrow-turn-up',
            condition: Boolean(parent),
            onClick: () => this.move(item, list, this.items, this.items.indexOf(parent) + 1),
          },
        ],
        [
          {
            label: this.$t('delete'),
            icon: 'fa-pro-light:trash',
            condition: !item.key,
            onClick: () => (this.remove = { item, parent }),
          },
        ],
      ]
        .map(group => group.filter(action => !('condition' in action) || action.condition))
        .filter(group => group.length)
    },

    emit() {
      this.items.forEach(item => (item.items || []).forEach(child => (child.placement = null)))
      this.$emit('input', { items: this.nested ? this.items : this.items.map(omit(['items'])) })
    },

    move(item, from, to, index) {
      from.splice(from.indexOf(item), 1)
      to.splice(index, 0, item)
      this.emit()
    },

    openItem({ item, parent } = {}) {
      this.modal = { item, parent }
    },

    submit(data) {
      const { item, parent } = this.modal

      if (item) Object.assign(item, data)
      else if (parent) parent.items.push(data)
      else this.items.push(data)

      this.modal = null
      this.emit()
    },

    confirmRemove() {
      const { item, parent } = this.remove
      const list = parent ? parent.items : this.items

      list.splice(list.indexOf(item), 1, ...item.items)

      this.remove = null
      this.emit()
    },

    canMove({ draggedContext, relatedContext, to }) {
      const isAllowed = !to.classList.contains('site-navigation__children') || this.canBeChild(draggedContext.element)

      this.rejected = isAllowed ? null : relatedContext.list
      return isAllowed
    },

    onChildrenDragOver(event) {
      if (this.dragging && !this.canBeChild(this.dragging)) event.stopPropagation()
    },

    onDragEnd({ from, to, oldIndex, newIndex }) {
      this.dragging = null
      this.rejected = null
      if (from === to && oldIndex === newIndex) return
      this.emit()
    },

    fetchPosts() {
      const url = new URL(location.origin + '/api/resource/post')
      url.searchParams.set('references', this.schemaData.id)
      url.searchParams.set('field.id', 'id')
      url.searchParams.set('field.label', 'title')
      url.searchParams.set('field.source', 'customFieldsConfig.0.source')
      url.searchParams.set('field.layout', 'layout')

      return fetch(url.toString(), { headers: { 'kvass-draft': true } })
        .then(res => res.json())
        .then(posts => (this.posts = posts.map(post => ({ ...post, schema: getSchema(post.source) }))))
    },

    fetchSchemas() {
      const sources = [...new Set(this.posts.map(post => post.source))]

      return Promise.all(
        sources.map(source =>
          graphql
            .request(CustomFieldSchemasQuery, { ids: [source] })
            .then(data => this.$set(this.schemas, source, data.CustomFieldSchemas[0].fields)),
        ),
      )
    },

    fetchUnits() {
      const url = new URL(location.origin + '/api/resource/residential')
      url.searchParams.set('project', this.schemaData.id)
      url.searchParams.set('field.id', 'id')
      url.searchParams.set('field.label', 'displayName|number')
      url.searchParams.set('sort.displayName', '1')

      return fetch(url.toString(), { headers: { 'kvass-draft': true } })
        .then(res => res.json())
        .then(units => (this.units = units.map(unit => ({ ...unit, label: String(unit.label) }))))
    },

    fetchCompanySettings() {
      const page = this.$path('publishing.relation.ref', this.schemaData)
      if (!page) return

      const url = new URL(location.origin + '/api/resource/page')
      url.searchParams.set('ids', page)
      url.searchParams.set('field.addHomeItem', 'customFields.projects-navigation.projects-add-home-menu-item')

      return fetch(url.toString())
        .then(res => res.json())
        .then(([settings]) => (this.hasHomeItem = Boolean(settings && settings.addHomeItem)))
    },
  },
  created() {
    this.promise = Promise.all([
      this.fetchPosts().then(this.fetchSchemas),
      this.type === 'Project' ? this.fetchUnits() : null,
      this.type === 'Project' ? this.fetchCompanySettings() : null,
    ])
  },
  components: {
    Draggable,
    ModalComponent,
    Card,
    ItemModal,
    Row,
  },
}
</script>

<style lang="scss">
.site-navigation {
  --site-navigation-columns: minmax(0, 1fr) minmax(0, 1fr) 2.5rem 2rem;

  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &--disabled .site-navigation-row {
    cursor: default;
  }

  &__header {
    display: grid;
    grid-template-columns: var(--site-navigation-columns);
    gap: 1rem;
    padding: 0 0.75rem 0 1rem;
    font-size: var(--font-size-small);
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    & > .site-navigation__item {
      border-radius: var(--border-radius);
      background-color: white;

      & > .site-navigation-row {
        border-radius: var(--border-radius) var(--border-radius) 0 0;
      }

      &:not(:has(.site-navigation__children > *)) > .site-navigation-row {
        border-radius: var(--border-radius);
      }

      & > .site-navigation-row .site-navigation-row__child-icon {
        display: none;
      }
    }
  }

  &__children {
    display: flex;
    flex-direction: column;

    & > .site-navigation__item,
    .site-navigation__add-child {
      border-top: 1px solid var(--light-grey);
    }

    .site-navigation-row__name {
      padding-left: 1.75rem;
    }

    .site-navigation-row__position {
      display: none;
    }

    &--rejected {
      border-radius: 0 0 var(--border-radius) var(--border-radius);
      outline: 1px dashed var(--error);
      outline-offset: -1px;
      background-color: css-alpha('error', 0.05);
    }
  }

  &__item {
    &--hidden .site-navigation-row__label {
      opacity: var(--opacity-muted);
    }

    &--ghost {
      opacity: 0.25;
    }
  }

  &__add-child {
    order: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.75rem;
    border: none;
    background-color: transparent;
    font: inherit;
    font-size: var(--font-size-small);
    text-align: left;
    cursor: pointer;

    &:hover {
      background-color: var(--lightest-grey);
    }
  }

  &__empty {
    padding: 2.5rem 1rem;
    border-radius: var(--border-radius);
    background-color: white;
    text-align: center;
  }

  &__add {
    flex-direction: column-reverse;
    align-items: center;
    justify-content: center;
    border: none !important;
    background-color: css-alpha('dark', 0.05) !important;

    .elder-button__label {
      flex-grow: 0;
      padding: 0.2rem 0 1rem;
    }

    .elder-button__icon {
      padding: 1rem 0 0;
      font-size: 1.6rem;
      color: var(--primary);
    }
  }
}
</style>
